import React, { FC, useCallback, useMemo, useState } from 'react';

import {
	BUTTON_TYPES,
	EvBodyMedium,
	EvDivider,
	EvFlexColumn,
	EvFlexRow,
	EvHeadingSmall,
	EvSvgIcon,
	Info,
	TEXT_COLOR,
	TITLE_MODES,
	TOAST_STATUS
} from '@evinced-private/ui-common';

import {
	LABEL_MANAGEMENT_PANEL_CANCEL_BUTTON_DAID,
	LABEL_MANAGEMENT_PANEL_DELETE_BUTTON_DAID,
	LABEL_MANAGEMENT_PANEL_SAVE_BUTTON_DAID
} from 'data-automation-ids/LabelManagementAutomationIds';
import { PlatformPopup } from 'src/common/components/platform-popup/PlatformPopup';
import { useNotifications } from 'src/common/providers/notificationsProvider/NotificationsConsumer';
import { Logger } from 'src/common/services/Logger';

import { Label, LabelType, LabelValue, PanelMode } from '../../types';
import { DiscardLabelChangesModal } from '../discard-label-changes-modal';

import { CoreLabelDetailsSection, CustomLabelDetailsSection } from './sections/label-details';
import { LabelValuesSection } from './sections/label-values';
import { LABEL_VALUES_LABELS } from './sections/label-values/LabelValuesSection.labels';
import { LABEL_PANEL_WIDTH } from './LabelPanel.constants';
import { LABEL_PANEL_LABELS } from './LabelPanel.labels';
import { LabelPanelProps } from './LabelPanel.types';
import {
	cloneLabel,
	computeCsvUploadUpdate,
	sortLabelValuesForDisplay,
	validateDescription,
	validateDisplayName,
	validateKey,
	validateNewValue
} from './LabelPanel.utils';

export const LabelPanel: FC<LabelPanelProps> = ({
	activeLabel,
	panelMode,
	productId,
	tenantId,
	isSaving,
	existingKeys,
	onSave,
	onClose,
	onDelete
}) => {
	const { toast } = useNotifications();
	const [editedLabel, setEditedLabel] = useState<Label>(() => cloneLabel(activeLabel));
	const [hasBeenTouched, setHasBeenTouched] = useState(false);
	const [displayNameError, setDisplayNameError] = useState<string | undefined>();
	const [descriptionError, setDescriptionError] = useState<string | undefined>();
	const [keyError, setKeyError] = useState<string | undefined>();
	const [csvUploadError, setCsvUploadError] = useState<string | undefined>();
	const [csvRejectedFileValues, setCsvRejectedFileValues] = useState<string[] | undefined>();
	const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

	const markTouched = useCallback(() => {
		setHasBeenTouched(true);
	}, []);

	const sortedValuesForTable = useMemo(
		() => sortLabelValuesForDisplay(editedLabel.values),
		[editedLabel.values]
	);

	const isCoreLabel = editedLabel.type === LabelType.CORE;
	const isAddMode = panelMode === PanelMode.ADD;
	const isCustomEditMode = !isAddMode && !isCoreLabel;

	const isValid = !displayNameError && !descriptionError && !keyError;
	const hasRequiredFields =
		!!editedLabel.displayName.trim() && (!isAddMode || !!editedLabel.key.trim());

	const canSave = useMemo(() => isValid && hasRequiredFields, [isValid, hasRequiredFields]);

	const handleKeyChange = useCallback(
		(value: string) => {
			markTouched();
			setEditedLabel((prev) => ({ ...prev, key: value }));
			setKeyError(validateKey(value, existingKeys));
		},
		[existingKeys, markTouched]
	);

	const handleDisplayNameChange = useCallback(
		(value: string) => {
			markTouched();
			setEditedLabel((prev) => ({ ...prev, displayName: value }));
			setDisplayNameError(validateDisplayName(value));
		},
		[markTouched]
	);

	const handleDescriptionChange = useCallback(
		(value: string) => {
			markTouched();
			setEditedLabel((prev) => ({ ...prev, description: value }));
			setDescriptionError(validateDescription(value));
		},
		[markTouched]
	);

	const clearCsvError = useCallback(() => {
		setCsvUploadError(undefined);
		setCsvRejectedFileValues(undefined);
	}, []);

	const handleAddValue = useCallback(
		(value: string) => {
			markTouched();
			const error = validateNewValue(value, editedLabel.values);
			if (error) {
				return error;
			}
			const newValue: LabelValue = {
				value: value.trim(),
				dashboardVisible: true,
				isSessionNew: true
			};
			clearCsvError();
			setEditedLabel((prev) => ({ ...prev, values: [...prev.values, newValue] }));
			return undefined;
		},
		[editedLabel?.values, markTouched, clearCsvError]
	);

	const showCsvSuccessToast = useCallback(
		(added: number, skipped: number) => {
			if (added > 0) {
				toast?.({
					show: true,
					status: TOAST_STATUS.SUCCESS,
					announcement: LABEL_VALUES_LABELS.CSV_UPLOAD_TOAST_ADDED(added, skipped)
				});
			} else if (skipped > 0) {
				toast?.({
					show: true,
					status: TOAST_STATUS.SUCCESS,
					announcement: LABEL_VALUES_LABELS.CSV_UPLOAD_TOAST_ALL_DUPLICATES(skipped)
				});
			}
		},
		[toast]
	);

	const handleUploadCsvContent = useCallback(
		(text: string) => {
			markTouched();
			clearCsvError();

			let uploadResult: ReturnType<typeof computeCsvUploadUpdate>['uploadResult'] | undefined;

			setEditedLabel((prev) => {
				const { nextLabel, uploadResult: result } = computeCsvUploadUpdate(text, prev);
				uploadResult = result;
				return nextLabel;
			});

			if (!uploadResult) {
				return;
			}
			if (uploadResult.emptyFile) {
				setCsvUploadError(LABEL_VALUES_LABELS.UPLOAD_EMPTY_FILE);
				return;
			}
			if (uploadResult.invalid.length > 0) {
				setCsvRejectedFileValues(uploadResult.rejectedFileValues ?? uploadResult.invalid);
				return;
			}

			showCsvSuccessToast(uploadResult.addedCount ?? 0, uploadResult.skippedDuplicateCount ?? 0);
		},
		[markTouched, clearCsvError, showCsvSuccessToast]
	);

	const handleRemoveValue = useCallback(
		(value: string) => {
			markTouched();
			setEditedLabel((prev) => ({
				...prev,
				values: prev.values.filter((v) => v.value !== value)
			}));
		},
		[markTouched]
	);

	const handleToggleDashboardVisibility = useCallback(
		(value: string) => {
			markTouched();
			setEditedLabel((prev) => ({
				...prev,
				values: prev.values.map((v) =>
					v.value === value ? { ...v, dashboardVisible: !(v.dashboardVisible ?? true) } : v
				)
			}));
		},
		[markTouched]
	);

	const handleSave = useCallback(async () => {
		try {
			await onSave(productId, tenantId, editedLabel);
			toast?.({
				show: true,
				status: TOAST_STATUS.SUCCESS,
				announcement: LABEL_PANEL_LABELS.SAVE_SUCCESS
			});
		} catch (error) {
			Logger.error('LabelPanel -> handleSave failed:', error);
			toast?.({
				show: true,
				status: TOAST_STATUS.FAIL,
				announcement: LABEL_PANEL_LABELS.SAVE_ERROR
			});
		}
	}, [productId, tenantId, onSave, toast, editedLabel]);

	const requestClose = useCallback(() => {
		if (hasBeenTouched) {
			setShowDiscardConfirm(true);
			return;
		}
		onClose();
	}, [hasBeenTouched, onClose]);

	const handleDiscardChanges = useCallback(() => {
		setShowDiscardConfirm(false);
		onClose();
	}, [onClose]);

	const handleKeepEditing = useCallback(() => {
		setShowDiscardConfirm(false);
	}, []);

	const { displayName, description } = editedLabel;

	const title = useMemo(
		() =>
			panelMode === PanelMode.EDIT
				? LABEL_PANEL_LABELS.TITLE_EDIT(displayName, isCoreLabel)
				: LABEL_PANEL_LABELS.TITLE_ADD,
		[panelMode, displayName, isCoreLabel]
	);

	const panelButtons = useMemo(() => {
		const cancelButton = {
			onClick: requestClose,
			title: LABEL_PANEL_LABELS.CANCEL,
			type: BUTTON_TYPES.GHOST,
			dataAutomationId: LABEL_MANAGEMENT_PANEL_CANCEL_BUTTON_DAID
		};
		const saveButton = {
			onClick: handleSave,
			title: LABEL_PANEL_LABELS.SAVE,
			type: BUTTON_TYPES.PRIMARY,
			disabled: !canSave || isSaving,
			dataAutomationId: LABEL_MANAGEMENT_PANEL_SAVE_BUTTON_DAID
		};

		const deleteButton = {
			onClick: onDelete,
			title: LABEL_PANEL_LABELS.DELETE_LABEL,
			ariaLabel: LABEL_PANEL_LABELS.DELETE_LABEL_ARIA(displayName),
			type: BUTTON_TYPES.SECONDARY,
			dataAutomationId: LABEL_MANAGEMENT_PANEL_DELETE_BUTTON_DAID
		};

		if (!isCustomEditMode) {
			return [cancelButton, saveButton];
		}

		return [deleteButton, cancelButton, saveButton];
	}, [isCustomEditMode, onDelete, displayName, requestClose, handleSave, canSave, isSaving]);

	return (
		<>
			<PlatformPopup
				popupWidth={LABEL_PANEL_WIDTH}
				isControlled
				isBgTransparent
				fullHeight
				horizontalPosition="right"
				titleMode={TITLE_MODES.NORMAL}
				title={title}
				isOpen
				onClose={requestClose}
				isPopupLoading={isSaving}
				buttons={panelButtons}
			>
				{isCoreLabel ? (
					<EvBodyMedium>{LABEL_PANEL_LABELS.SUBTITLE_CORE_EDIT}</EvBodyMedium>
				) : (
					<EvFlexRow gap="SMALL" align="CENTER">
						<EvSvgIcon icon={Info} isSmall />
						<EvBodyMedium>{LABEL_PANEL_LABELS.SUBTITLE_CUSTOM_EDIT}</EvBodyMedium>
					</EvFlexRow>
				)}
				<EvFlexRow padding={['XLARGE', 'NONE']}>
					<EvDivider variant="dashed" />
				</EvFlexRow>
				<EvFlexColumn gap="XLARGE">
					{isCoreLabel ? (
						<CoreLabelDetailsSection
							displayName={displayName}
							description={description ?? ''}
							onDisplayNameChange={handleDisplayNameChange}
							onDescriptionChange={handleDescriptionChange}
							displayNameError={displayNameError}
							descriptionError={descriptionError}
						/>
					) : (
						<CustomLabelDetailsSection
							displayName={displayName}
							description={description ?? ''}
							labelKey={editedLabel.key}
							isKeyEditable={isAddMode}
							keyError={keyError}
							onKeyChange={handleKeyChange}
							onDisplayNameChange={handleDisplayNameChange}
							onDescriptionChange={handleDescriptionChange}
							displayNameError={displayNameError}
							descriptionError={descriptionError}
						/>
					)}
					<EvFlexRow padding={['SMALL', 'NONE']}>
						<EvDivider variant="dashed" />
					</EvFlexRow>
					<EvFlexColumn gap="LARGE">
						<EvHeadingSmall as="h3" color={TEXT_COLOR.CONTENT_2}>
							{LABEL_VALUES_LABELS.SECTION_TITLE}
						</EvHeadingSmall>
						<LabelValuesSection
							values={sortedValuesForTable}
							showDashboardVisibility={!isAddMode && isCoreLabel}
							onAddValue={handleAddValue}
							onRemoveValue={handleRemoveValue}
							onToggleDashboardVisibility={handleToggleDashboardVisibility}
							onUploadCsvContent={handleUploadCsvContent}
							onFormTouched={markTouched}
							csvUploadError={csvUploadError}
							csvRejectedFileValues={csvRejectedFileValues}
							onDismissCsvError={clearCsvError}
						/>
					</EvFlexColumn>
				</EvFlexColumn>
			</PlatformPopup>
			{showDiscardConfirm && (
				<DiscardLabelChangesModal
					onDiscard={handleDiscardChanges}
					onKeepEditing={handleKeepEditing}
				/>
			)}
		</>
	);
};
